// Time-entry corrections — the three surfaces.
//
//   CorrectionModal   the employee asks for a day to be changed
//   CorrectionRows    the approver's rows, rendered inside HR's approval queue
//
// What they have asked for, and how it went, is no longer a card here: it shows
// inside the day it is about (DayClockLog) and in My Space (MyRequests).
//
// TeamCorrections — a card wrapping CorrectionRows with its own header — stood
// here until the approval queue learned to render corrections itself (#107).
// Keeping both drew every pending row twice on one tab, each copy with a live
// Approve/Decline pair.
//
// Presentational: rows come from the screen (which loads them through
// lib/timeCorrections in its refresh), mutations go back out through the same
// module. That is what lets the colocated test mock one module instead of a
// chainable PostgREST builder.
import { useId, useMemo, useState } from 'react';
import { Avatar, Badge, Button, Mono } from './primitives';
import { Eyebrow } from './card';
import { Dialog } from './dialog';
import { Field, TextArea, TextInput } from './field';
import { InlineStatus, useToast } from './feedback';
import { fmtDayKey, fmtRelative, toOrgWallTime } from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import {
  cancelCorrection,
  decideCorrection,
  fileCorrection,
  correctionSummary,
  fmtDelta,
  type Correction,
  type PendingCorrection,
} from '../lib/timeCorrections';

/** The subset of a time entry this feature needs. */
export type CorrectableEntry = {
  id: string;
  project_id: string | null;
  seconds: number;
  status: 'running' | 'paused' | 'logged';
  system_closed?: boolean;
  /** The check-in. started_at is only the latest segment's start. */
  created_at: string;
  /** The check-out; null while running. */
  ended_at?: string | null;
};

const ADD_NEW = 'new';

// A day's recorded time, to the minute a correction is asked for in: "7h 30m".
// It was h:mm ("7:30") beside a clock in m:ss, the same shape for two units (TS-03).
const hm = (seconds: number): string => fmtDuration(seconds, 'hm');
const dayLabel = (iso: string): string => fmtDayKey(iso, { weekday: true });

/** Which control a validation message belongs to; 'form' is the server's answer. */
type Problem = { at: 'in' | 'out' | 'reason' | 'form'; message: string };

// ── Request a correction ──────────────────────────────────────────────────────
// On the shared Dialog: it takes focus when it opens, keeps Tab inside, hands
// focus back to the day total that opened it, and asks before throwing away a
// typed reason (TS-V1, ME-08). Each refusal is tied to the field it is about
// and announced (TS-V2); it used to be one unannounced line above the buttons.
export function CorrectionModal({
  uid,
  workDate,
  entries,
  tz,
  pending,
  onClose,
  onChanged,
}: {
  uid: string;
  /** The org-day being corrected, as an ISO date key. */
  workDate: string;
  /** That day's entries, already bucketed by the caller. */
  entries: CorrectableEntry[];
  /** The org's zone: the times asked for are on its clock, as the server reads them. */
  tz: string;
  /** An existing open request for this day, if there is one. */
  pending?: Correction;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const entryLabel = useId();
  // Day-clock only: project time feeds billing and stays senior-edited.
  const dayClock = useMemo(() => entries.filter((e) => e.project_id === null), [entries]);
  const projectCount = entries.length - dayClock.length;
  const correctable = useMemo(() => dayClock.filter((e) => e.status === 'logged'), [dayClock]);

  // The org-clock HH:MM of an instant, or '' when it is not on this day — a
  // session closed overnight ends at 00:15 tomorrow, which is never the answer.
  const wall = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const w = toOrgWallTime(iso, tz);
    return w.slice(0, 10) === workDate ? w.slice(11) : '';
  };
  // What each session's fields start at. A session the system closed overnight
  // starts with no check-out: "when did you actually leave?" is the question.
  const start = (e: CorrectableEntry | undefined) => ({
    in: wall(e?.created_at),
    out: e?.system_closed ? '' : wall(e?.ended_at),
  });

  const [choice, setChoice] = useState<string>(correctable[0]?.id ?? ADD_NEW);
  const selected = correctable.find((e) => e.id === choice) ?? null;
  const initial = start(selected ?? undefined);
  const [inTime, setInTime] = useState(initial.in);
  const [outTime, setOutTime] = useState(initial.out);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  const [busy, setBusy] = useState(false);

  // Prefill from whichever session is picked, so the times start at the truth.
  const pick = (key: string) => {
    const s = start(correctable.find((e) => e.id === key));
    setChoice(key);
    setInTime(s.in);
    setOutTime(s.out);
    setProblem(null);
  };

  const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const span = inTime && outTime ? (mins(outTime) - mins(inTime)) * 60 : null;
  const original = start(selected ?? undefined);
  const changed = inTime !== original.in || outTime !== original.out;
  const errorAt = (at: Problem['at']) => (problem?.at === at ? problem.message : undefined);

  const submit = async () => {
    if (busy) return;
    const refuse = (at: Problem['at'], message: string) => setProblem({ at, message });
    if (!selected && choice !== ADD_NEW)
      return refuse('form', 'That session is still running — check out first, then correct it.');
    if (!inTime) return refuse('in', 'When did you check in?');
    if (!outTime) return refuse('out', 'When did you check out?');
    if (span !== null && span <= 0) return refuse('out', 'Check-out has to be after check-in.');
    if (selected && !changed)
      return refuse('out', 'That is already what is recorded. Change a time first.');
    if (!reason.trim())
      return refuse('reason', 'Say what happened — whoever approves this only sees this line.');
    setBusy(true);
    setProblem(null);
    try {
      await fileCorrection({
        profileId: uid,
        entryId: choice === ADD_NEW ? null : choice,
        workDate,
        // A kept check-in is sent as null so the entry keeps its exact second.
        checkIn: selected && inTime === original.in ? null : inTime,
        checkOut: outTime,
        reason,
      });
      onChanged();
      onClose();
      toast.push({
        tone: 'success',
        message: `Correction requested for ${dayLabel(workDate)}. Your hours change once HR approves it.`,
      });
    } catch (e) {
      refuse('form', (e as { message?: string }).message || 'Could not send that request.');
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      await cancelCorrection(pending.id);
      onChanged();
      onClose();
      toast.push({ tone: 'success', message: 'Correction request cancelled.' });
    } catch (e) {
      setProblem({
        at: 'form',
        message: (e as { message?: string }).message || 'Could not cancel that request.',
      });
      setBusy(false);
    }
  };

  const row = (
    key: string,
    title: React.ReactNode,
    sub: React.ReactNode,
    disabled?: boolean,
  ): React.ReactNode => (
    <label
      key={key}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--hf-space-3)',
        padding: 'var(--hf-space-2) var(--hf-space-3)',
        borderRadius: 'var(--hf-radius-md)',
        border: `1px solid ${choice === key && !disabled ? 'var(--hf-accent-line)' : 'var(--hf-rule-2)'}`,
        background: choice === key && !disabled ? 'var(--hf-accent-wash)' : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <input
        type="radio"
        name="hf-correction-entry"
        checked={choice === key}
        disabled={disabled}
        onChange={() => pick(key)}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 'var(--hf-text-md)',
            color: disabled ? 'var(--hf-ink-3)' : 'var(--hf-ink)',
          }}
        >
          {title}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
          {sub}
        </span>
      </span>
    </label>
  );

  // "10:02 → 18:30", with an overnight close said in words, not as a time.
  const spanLabel = (e: CorrectableEntry) =>
    `${wall(e.created_at) || '—'} → ${e.system_closed ? 'closed overnight' : wall(e.ended_at) || 'next day'}`;

  const day = (
    <div
      style={{
        fontSize: 'var(--hf-text-sm)',
        color: 'var(--hf-ink-2)',
        marginBottom: 'var(--hf-space-4)',
      }}
    >
      {dayLabel(workDate)}
    </div>
  );
  const formError = errorAt('form') && <InlineStatus tone="error">{errorAt('form')}</InlineStatus>;

  if (pending)
    // The one-open-request-per-day index would refuse a second one anyway;
    // saying so beats surfacing a constraint violation.
    return (
      <Dialog
        open
        title="Request a time correction"
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button variant="danger" onClick={() => void cancelPending()} loading={busy}>
              Cancel request
            </Button>
          </>
        }
      >
        {day}
        <div
          style={{
            fontSize: 'var(--hf-text-md)',
            color: 'var(--hf-ink-2)',
            lineHeight: 'var(--hf-leading-body)',
            marginBottom: 'var(--hf-space-3)',
          }}
        >
          You already have a request waiting for HR on this day:{' '}
          <Mono style={{ fontSize: 'var(--hf-text-sm)' }}>{correctionSummary(pending)}</Mono>
          <div style={{ marginTop: 'var(--hf-space-2)', color: 'var(--hf-ink-3)' }}>
            “{pending.reason}”
          </div>
        </div>
        {formError}
      </Dialog>
    );

  return (
    <Dialog
      open
      title="Request a time correction"
      onClose={onClose}
      dirty={reason.trim() !== '' || changed}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" icon="check" onClick={() => void submit()} loading={busy}>
            Send request
          </Button>
        </>
      )}
    >
      {day}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
          <Eyebrow as="div" id={entryLabel}>
            Which session?
          </Eyebrow>
          <div
            role="radiogroup"
            aria-labelledby={entryLabel}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}
          >
            {correctable.map((e) =>
              row(
                e.id,
                <>
                  Day clock · <Mono style={{ fontSize: 'var(--hf-text-sm)' }}>{spanLabel(e)}</Mono>
                </>,
                <>
                  {hm(e.seconds)} recorded
                  {e.system_closed ? ' · you forgot to check out — set when you left' : ''}
                </>,
              ),
            )}
            {dayClock
              .filter((e) => e.status !== 'logged')
              .map((e) => row(e.id, 'Day clock', 'still running — check out first', true))}
            {/* "checked in": the day clock's own verb (UI-CONVENTIONS Vocabulary).
                A session beside the day's others is fine: the server refuses
                only one that overlaps (20261009100000). */}
            {row(
              ADD_NEW,
              'A session I never checked in for',
              'add it by the times you came and left',
              false,
            )}
          </div>
          {projectCount > 0 && (
            <div style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
              {/* "1 project entry … are not shown", and "changed by your lead":
                  there is no lead surface for it while Projects is off (TS-10). */}
              {projectCount === 1
                ? '1 project entry on this day is not shown'
                : `${projectCount} project entries on this day are not shown`}{' '}
              — HR changes project time.
            </div>
          )}
        </div>

        {/* Times, not hours: people remember when they left, not how long they
            stayed (owner, 2026-10-09). The server works the hours out, less
            the session's breaks. */}
        <div style={{ display: 'flex', gap: 'var(--hf-space-3)', alignItems: 'flex-start' }}>
          <Field label="Checked in" error={errorAt('in')} style={{ flex: '1 1 0' }}>
            <TextInput type="time" mono value={inTime} onChange={setInTime} />
          </Field>
          <Field label="Checked out" error={errorAt('out')} style={{ flex: '1 1 0' }}>
            <TextInput type="time" mono value={outTime} onChange={setOutTime} />
          </Field>
        </div>
        {span !== null && span > 0 && (
          <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
            {hm(span)} between those times
            {selected ? `, less any breaks — was ${hm(selected.seconds)}` : ''}
          </div>
        )}

        <Field
          label="What happened?"
          required
          error={errorAt('reason')}
          hint="Your hours do not change until HR approves this."
        >
          <TextArea
            value={reason}
            onChange={setReason}
            placeholder="e.g. Forgot to check out — I left at 6:30."
          />
        </Field>

        {formError}
      </div>
    </Dialog>
  );
}

// ── The approver queue ────────────────────────────────────────────────────────
/**
 * The rows on their own, with no card around them.
 *
 * Split out so the approval queue can render corrections beside leave requests
 * under one set of Pending/Approved/Declined filters. A decided correction used to
 * have nowhere to appear at all — the old listPending() answered "what needs me
 * now", and the queue's own Approved/Declined counts were leave-only, so approving
 * one made it vanish from the product entirely.
 */
export function CorrectionRows({
  rows,
  uid,
  onChanged,
}: {
  rows: PendingCorrection[];
  uid: string;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const decide = async (r: PendingCorrection, status: 'approved' | 'rejected') => {
    setBusy(r.id);
    setErr(null);
    const { error } = await decideCorrection(r.id, status, uid);
    if (error) setErr(error.message);
    else
      toast.push({
        tone: 'success',
        message: `${status === 'approved' ? 'Approved' : 'Declined'} ${r.full_name ? r.full_name + '’s' : 'the'} correction for ${dayLabel(r.work_date)}.`,
      });
    onChanged();
    setBusy(null);
  };

  return (
    <>
      {err && (
        <div style={{ marginBottom: 'var(--hf-space-2)' }}>
          <InlineStatus tone="error">{err}</InlineStatus>
        </div>
      )}

      {rows.map((r) => {
        const own = r.profile_id === uid;
        return (
          <div
            key={r.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--hf-space-2) var(--hf-space-3)',
              padding: 'var(--hf-space-3) 0',
              borderTop: '1px solid var(--hf-rule)',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--hf-space-2)',
                minWidth: 0,
                flex: '0 1 190px',
              }}
            >
              <Avatar name={r.full_name ?? undefined} size={26} />
              <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
                {r.full_name ?? 'Someone'}
              </span>
            </span>

            <span style={{ minWidth: 0, flex: '1 1 220px', fontSize: 'var(--hf-text-sm)' }}>
              <span style={{ color: 'var(--hf-ink-2)' }}>{dayLabel(r.work_date)}</span>{' '}
              <Mono style={{ fontSize: 'var(--hf-text-xs)' }}>{correctionSummary(r)}</Mono>{' '}
              <span style={{ color: 'var(--hf-amber-text)' }}>
                {fmtDelta(r.requested_seconds - r.current_seconds)}
              </span>
              {/* The reason is what the approver decides on: it wraps, it is
                  never cut to one line behind an ellipsis (UI-CONVENTIONS §4). */}
              <span
                style={{ display: 'block', color: 'var(--hf-ink-3)', overflowWrap: 'anywhere' }}
              >
                {r.reason}
              </span>
            </span>

            {r.status !== 'pending' ? (
              // A decided row used to render Approve/Decline exactly like a
              // pending one, so the queue's Approved and Declined chips showed
              // rows you could not tell apart from work still waiting — and
              // clicking either raised "a decided correction can no longer be
              // changed" from the trigger. Same shape as the leave row beside
              // this one.
              //
              // No Revert, unlike leave: enforce_time_correction refuses to
              // re-decide outright ("an approved correction has already moved
              // real hours"), so the button would only ever offer an error.
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2)',
                  flexShrink: 0,
                }}
              >
                {r.decided_at && (
                  <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>
                    {fmtRelative(r.decided_at)}
                  </span>
                )}
                <Badge tone={r.status === 'approved' ? 'success' : 'danger'}>
                  {r.status === 'approved' ? 'Approved' : 'Declined'}
                </Badge>
              </span>
            ) : own ? (
              // The database refuses this outright; disabling it here explains
              // why rather than letting them find out by clicking.
              <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                yours — another approver decides
              </span>
            ) : (
              // A decision is a phone task: each is a 44px target under a finger.
              <span style={{ display: 'inline-flex', gap: 'var(--hf-space-2)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hf-hit"
                  disabled={busy === r.id}
                  onClick={() => void decide(r, 'rejected')}
                >
                  Decline
                </Button>
                <Button
                  variant="accent"
                  size="sm"
                  className="hf-hit"
                  icon="check"
                  disabled={busy === r.id}
                  onClick={() => void decide(r, 'approved')}
                >
                  Approve
                </Button>
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}
